// apps/spa/src/lib/api.ts
// Centralized fetch wrapper for /api/* calls.
//
// Contract: parses { error: { code, message, details? } } body shape from guardian-worker.
// Side effects (in addition to throwing ApiError):
//   - 401 / SESSION_EXPIRED -> document.dispatchEvent(new CustomEvent('session-expired'))
//   - ROLE_REQUIRED         -> window.location.href = '/auth/role-required'
//
// Source: RESEARCH Pattern 9 (verbatim) + ERROR_CODES from @guardian/shared
// (NEVER inline string literals — keeps codes in lockstep with the worker).

import { ERROR_CODES } from '@guardian/shared';

export class ApiError extends Error {
  status: number;
  code: string;
  details?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers ?? {}),
    },
    credentials: 'same-origin',
  });

  if (res.ok) {
    return (await res.json()) as T;
  }

  // Non-2xx — parse the canonical { error: { code, message, details? } } envelope.
  let body: { error?: { code?: string; message?: string; details?: unknown } } = {};
  try {
    body = (await res.json()) as typeof body;
  } catch {
    /* body was not JSON — fall through with empty object */
  }

  const code = body?.error?.code ?? 'unknown';
  const message = body?.error?.message ?? `HTTP ${res.status}`;

  // Global side-effects in addition to throwing:
  if (res.status === 401 || code === ERROR_CODES.SESSION_EXPIRED) {
    document.dispatchEvent(new CustomEvent('session-expired'));
  }
  if (code === ERROR_CODES.ROLE_REQUIRED) {
    window.location.href = '/auth/role-required';
  }

  throw new ApiError(res.status, code, message, body?.error?.details);
}
